import React from "react";
import { useNavigate } from "react-router-dom";

const UpcomingFeatures = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: "🛏️",
      title: "Online Bed Booking",
      description:
        "Search available beds, view room details, compare prices and book your preferred bed online.",
      status: "Coming Soon",
      category: "Accommodation",
    },
    {
      icon: "📋",
      title: "Application Tracking History",
      description:
        "Track your application status, view previous applications and monitor every stage of the process.",
      status: "Coming Soon",
      category: "Applications",
    },
    {
      icon: "🏦",
      title: "Loan Process & Bank Requirements",
      description:
        "Understand loan requirements, required documents, eligibility information and application steps from participating banks.",
      status: "Coming Soon",
      category: "Banking",
    },
    {
      icon: "💰",
      title: "Instant Money Services",
      description:
        "Access eligible third-party financial services and explore available money or financing options.",
      status: "Coming Soon",
      category: "Financial Services",
    },
    {
      icon: "🌳",
      title: "Land & Plot Sales",
      description:
        "Explore available land and plots, check property details and connect with sellers or property partners.",
      status: "Coming Soon",
      category: "Real Estate",
    },
    {
      icon: "🏠",
      title: "Rented House Search",
      description:
        "Search rental houses and properties based on location, budget, property type and other preferences.",
      status: "Coming Soon",
      category: "Real Estate",
    },
    {
      icon: "📄",
      title: "Online Document Services",
      description:
        "Access document-related services and keep track of submitted requests and application documents.",
      status: "Coming Soon",
      category: "Services",
    },
    {
      icon: "🚗",
      title: "Vehicle & Transport Services",
      description:
        "Explore future services related to vehicles, transportation and other mobility requirements.",
      status: "Coming Soon",
      category: "Transport",
    },
    {
      icon: "🔔",
      title: "More Services",
      description:
        "Additional digital services and partner integrations will be introduced as the platform grows.",
      status: "Planned",
      category: "More",
    },
  ];

  const handleBack = () => {
    navigate("/dashboard");
  };

  return (
    <div style={styles.page}>
      {/* ================================================= */}
      {/* HEADER */}
      {/* ================================================= */}

      <div style={styles.header}>
        <button type="button" style={styles.backButton} onClick={handleBack}>
          ← Back
        </button>

        <div style={styles.headerContent}>
          <div style={styles.headerIcon}>🚀</div>

          <div>
            <h1 style={styles.title}>Upcoming Features</h1>

            <p style={styles.subtitle}>
              New services and digital experiences coming soon to SVK.
            </p>
          </div>
        </div>
      </div>

      {/* ================================================= */}
      {/* INTRO CARD */}
      {/* ================================================= */}

      <div style={styles.introCard}>
        <div style={styles.introIcon}>✨</div>

        <div>
          <h2 style={styles.introTitle}>Something exciting is coming!</h2>

          <p style={styles.introText}>
            We are working on multiple services to make accommodation,
            applications, financial services and property searches easier from
            one platform.
          </p>
        </div>
      </div>

      {/* ================================================= */}
      {/* FEATURES */}
      {/* ================================================= */}

      <div style={styles.sectionHeader}>
        <div>
          <h2 style={styles.sectionTitle}>Services in Development</h2>

          <p style={styles.sectionSubtitle}>
            Explore the services planned for the upcoming releases.
          </p>
        </div>

        <div style={styles.featureCount}>{features.length} Features</div>
      </div>

      <div style={styles.grid}>
        {features.map((feature, index) => (
          <div
            key={index}
            style={styles.card}
            className="upcoming-feature-card"
          >
            {/* CARD TOP */}

            <div style={styles.cardTop}>
              <div style={styles.iconContainer}>{feature.icon}</div>

              <span
                style={
                  feature.status === "Planned"
                    ? styles.plannedBadge
                    : styles.comingSoonBadge
                }
              >
                {feature.status}
              </span>
            </div>

            {/* CATEGORY */}

            <div style={styles.category}>{feature.category}</div>

            {/* TITLE */}

            <h3 style={styles.cardTitle}>{feature.title}</h3>

            {/* DESCRIPTION */}

            <p style={styles.description}>{feature.description}</p>

            {/* FOOTER */}

            <div style={styles.cardFooter}>
              <span style={styles.learnMore}>Coming Soon</span>

              <span style={styles.arrow}>→</span>
            </div>
          </div>
        ))}
      </div>

      {/* ================================================= */}
      {/* BOTTOM MESSAGE */}
      {/* ================================================= */}

      <div style={styles.bottomCard}>
        <div style={styles.bottomIcon}>💡</div>

        <div>
          <h3 style={styles.bottomTitle}>
            Building more than just a booking platform
          </h3>

          <p style={styles.bottomText}>
            SVK is expanding into a broader digital services platform connecting
            users with accommodation, applications, financial services, real
            estate and other useful services.
          </p>
        </div>
      </div>

      {/* ================================================= */}
      {/* CSS */}
      {/* ================================================= */}

      <style>
        {`
          .upcoming-feature-card {
            transition:
              transform 0.25s ease,
              box-shadow 0.25s ease;
          }

          .upcoming-feature-card:hover {
            transform: translateY(-6px);
            box-shadow:
              0 18px 35px rgba(15, 23, 42, 0.12);
          }

          .upcoming-feature-card:hover .arrow {
            transform: translateX(5px);
          }

          @media (max-width: 768px) {
            .upcoming-feature-grid {
              grid-template-columns: 1fr;
            }
          }
        `}
      </style>
    </div>
  );
};

export default UpcomingFeatures;

// =========================================================
// STYLES
// =========================================================

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)",
    padding: "24px",
    boxSizing: "border-box",
  },

  // =======================================================
  // HEADER
  // =======================================================

  header: {
    maxWidth: "1250px",
    margin: "0 auto 24px",
  },

  backButton: {
    border: "none",
    background: "#475569",
    color: "#ffffff",
    padding: "9px 18px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    marginBottom: "20px",
    boxShadow: "0 4px 10px rgba(15, 23, 42, 0.12)",
  },

  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: "18px",
  },

  headerIcon: {
    width: "65px",
    height: "65px",
    borderRadius: "18px",
    background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "32px",
    boxShadow: "0 10px 25px rgba(79, 70, 229, 0.25)",
    flexShrink: 0,
  },

  title: {
    margin: "0",
    color: "#111827",
    fontSize: "30px",
    fontWeight: "800",
  },

  subtitle: {
    margin: "6px 0 0",
    color: "#64748b",
    fontSize: "15px",
  },

  // =======================================================
  // INTRO
  // =======================================================

  introCard: {
    maxWidth: "1250px",
    margin: "0 auto 30px",
    padding: "22px",
    background: "#ffffff",
    borderRadius: "16px",
    border: "1px solid rgba(99, 102, 241, 0.12)",
    boxShadow: "0 8px 25px rgba(15, 23, 42, 0.06)",
    display: "flex",
    alignItems: "center",
    gap: "18px",
    boxSizing: "border-box",
  },

  introIcon: {
    width: "50px",
    height: "50px",
    borderRadius: "14px",
    background: "#eef2ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "25px",
    flexShrink: 0,
  },

  introTitle: {
    margin: "0 0 5px",
    fontSize: "18px",
    fontWeight: "700",
    color: "#1e293b",
  },

  introText: {
    margin: "0",
    color: "#64748b",
    lineHeight: "1.6",
    fontSize: "14px",
  },

  // =======================================================
  // SECTION
  // =======================================================

  sectionHeader: {
    maxWidth: "1250px",
    margin: "0 auto 18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "15px",
  },

  sectionTitle: {
    margin: "0",
    fontSize: "21px",
    fontWeight: "750",
    color: "#1e293b",
  },

  sectionSubtitle: {
    margin: "5px 0 0",
    color: "#64748b",
    fontSize: "13px",
  },

  featureCount: {
    background: "#e0e7ff",
    color: "#4338ca",
    padding: "7px 14px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "700",
    whiteSpace: "nowrap",
  },

  // =======================================================
  // GRID
  // =======================================================

  grid: {
    maxWidth: "1250px",
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "20px",
  },

  // =======================================================
  // CARD
  // =======================================================

  card: {
    background: "#ffffff",
    borderRadius: "18px",
    padding: "22px",
    minHeight: "270px",
    boxSizing: "border-box",
    border: "1px solid rgba(148, 163, 184, 0.18)",
    boxShadow: "0 8px 25px rgba(15, 23, 42, 0.06)",
    display: "flex",
    flexDirection: "column",
  },

  cardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    marginBottom: "16px",
  },

  iconContainer: {
    width: "52px",
    height: "52px",
    borderRadius: "15px",
    background: "linear-gradient(135deg, #eef2ff, #f5f3ff)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "27px",
  },

  comingSoonBadge: {
    background: "#fff7ed",
    color: "#c2410c",
    border: "1px solid #fed7aa",
    padding: "5px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "700",
  },

  plannedBadge: {
    background: "#f1f5f9",
    color: "#475569",
    border: "1px solid #cbd5e1",
    padding: "5px 10px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "700",
  },

  category: {
    color: "#6366f1",
    fontSize: "11px",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.7px",
    marginBottom: "7px",
  },

  cardTitle: {
    margin: "0 0 10px",
    color: "#1e293b",
    fontSize: "18px",
    fontWeight: "750",
  },

  description: {
    margin: "0",
    color: "#64748b",
    fontSize: "13px",
    lineHeight: "1.65",
    flex: 1,
  },

  cardFooter: {
    marginTop: "20px",
    paddingTop: "14px",
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },

  learnMore: {
    color: "#6366f1",
    fontSize: "12px",
    fontWeight: "700",
  },

  arrow: {
    color: "#6366f1",
    fontSize: "20px",
    fontWeight: "700",
    transition: "transform 0.25s ease",
  },

  // =======================================================
  // BOTTOM
  // =======================================================

  bottomCard: {
    maxWidth: "1250px",
    margin: "30px auto 0",
    padding: "24px",
    background: "linear-gradient(135deg, #312e81, #4f46e5)",
    color: "#ffffff",
    borderRadius: "18px",
    display: "flex",
    alignItems: "center",
    gap: "18px",
    boxShadow: "0 12px 30px rgba(49, 46, 129, 0.22)",
    boxSizing: "border-box",
  },

  bottomIcon: {
    width: "50px",
    height: "50px",
    borderRadius: "14px",
    background: "rgba(255,255,255,0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "25px",
    flexShrink: 0,
  },

  bottomTitle: {
    margin: "0 0 6px",
    fontSize: "18px",
    fontWeight: "750",
  },

  bottomText: {
    margin: "0",
    fontSize: "13px",
    lineHeight: "1.6",
    opacity: 0.9,
  },
};
